import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import { getOptimizedImageUrl } from '../lib/cms';

export default function TarotkortterapiPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [location]);

  return (
    <>
      <SeoHead
        title="Tarotkortterapi – Lone Hvid | Ibjerget på Midtfyn"
        description="Tarotkortterapi v. Lone Hvid på Ibjerget. Terapeutisk samtale og selvindsigt med to behandlinger: Status på dine chakraer og Fokus på problemløsning."
        canonicalUrl="https://www.ibjerget.dk/tarotkortterapi"
      />

      {/* Hero */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 bg-[#381d6e] text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/tarotkortterapi.webp', { width: 1400, quality: 80, fit: 'cover' })}
            alt="Tarotkort placeret ved chakramateriale på Ibjerget"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#381d6e] via-[#381d6e]/75 to-transparent" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#d9238c]/30 border border-[#d9238c]/40 text-[#f7eefa] text-xs font-semibold uppercase tracking-wider mb-6">
            <i className="fa-solid fa-compass" aria-hidden="true"></i>
            <span>Terapeut Lone Hvid</span>
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold text-[#faf5f9] tracking-tight leading-tight">
            Tarotkortterapi
          </h1>
          <p className="mt-6 text-base sm:text-xl text-[#f7eefa] max-w-3xl mx-auto leading-relaxed font-normal">
            Kortene som et dybt terapeutisk spejl for dine følelser, indsigter og personlige udvikling.
          </p>

          {/* Quick jump to the two treatments */}
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href="#chakraer"
              className="px-6 py-3.5 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-sm shadow-md transition-colors flex items-center gap-2"
            >
              <span>1. Status på dine chakraer</span>
              <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true"></i>
            </a>
            <a
              href="#problemloesning"
              className="px-6 py-3.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20 transition-colors flex items-center gap-2"
            >
              <span>2. Fokus på problemløsning</span>
              <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Intro by Lone Hvid */}
      <section className="py-16 bg-[#faf5f9] border-b border-[#edd5e8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 sm:p-10 rounded-3xl border border-[#edd5e8] shadow-sm space-y-4">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-[#faf5f9] flex items-center justify-center text-[#d9238c] font-serif font-bold text-sm">
                LH
              </span>
              <div>
                <h2 className="font-serif text-xl font-semibold text-[#1f142e]">
                  Om Tarotkort-terapi hos Lone Hvid
                </h2>
                <p className="text-xs text-[#9b1b66]">NLP-terapeut, kursusleder & PD i psykologi</p>
              </div>
            </div>

            <div className="prose prose-stone text-[#4e3568] text-base leading-relaxed space-y-4 pt-2">
              <p>
                Jeg er uddannet NLP-terapeut og -kursusleder (i alt 3-årig uddannelse), og jeg har PD i psykologi, ud over mange andre uddannelser. Herudover har jeg i ca. 40 år arbejdet med symboler, visdom og forskellige kulturers metoder til udvikling af mennesket.
              </p>
              <p>
                Jeg har valgt Tarotkort som metode, fordi de er virkeligt gode at spejle sig i, og netop derfor kan man bruge dem som terapeutiske redskaber. De går dybt ned i følelser og problemers kerner, når man arbejder med dem på den måde. De bringer indsigter fra dit ubevidste til dit bevidste sind, og derudfra kan du med denne nye bevidsthed arbejde med dig selv.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BEHANDLING 1: STATUS PÅ DINE CHAKRAER */}
      <section id="chakraer" className="py-20 sm:py-24 bg-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#faf5f9] text-[#9b1b66] text-xs font-semibold uppercase tracking-wider mb-4">
            <i className="fa-solid fa-sparkles text-[11px]" aria-hidden="true"></i>
            <span>Behandling 1</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            <div className="lg:col-span-7 space-y-6">
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#1f142e] tracking-tight">
                Status på dine chakraer
              </h2>

              <div className="prose prose-stone text-[#4e3568] text-base leading-relaxed space-y-4">
                <p>
                  Et chakra er et knudepunkt for bestemte energier i din krop, som ikke er fysiske, men som virker ud fra og ind på dit sind på forskellige områder.
                </p>
                <p>
                  Her gennemgår vi alle dine 9 chakraer og ser, hvor der er flow, og hvor der kan gemme sig problemer/hindringer, som vi kan finde løsninger på. Det er en dyb rejse ind i dig selv – og ind imellem kan det være hensigtsmæssigt at lave terapeutiske øvelser for at komme videre. Du får "skjulte" indsigter om dig selv, og hvordan dit system har det på de områder, der hører til det enkelte chakra. Nogle klienter kan næsten ikke vente med at trække det næste kort – en spændende måde at se og opdage sine chakraer på. Så spring ud i det og bliv meget klogere på dig selv.
                </p>
              </div>

              <div className="p-5 bg-[#fcf8fb] rounded-2xl border border-[#edd5e8] text-sm text-[#4e3568] space-y-2">
                <p className="font-semibold text-[#1f142e]">
                  Hvad du oplever i en chakra-session:
                </p>
                <ul className="space-y-1.5 text-xs sm:text-sm">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#d9238c]" aria-hidden="true"></i>
                    <span>Gennemgang af alle 9 chakraer for energetisk flow og eventuelle blokeringer</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#d9238c]" aria-hidden="true"></i>
                    <span>Terapeutiske redskaber og øvelser tilpasset det enkelte chakra</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#d9238c]" aria-hidden="true"></i>
                    <span>Dybdegående selvindsigt og forståelse for dit eget system</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right: Foto & Praktisk boks for Chakraer */}
            <div className="lg:col-span-5 space-y-6">
              
              {/* Photo: tarotkortterapi.webp */}
              <div className="rounded-3xl overflow-hidden shadow-md border border-[#edd5e8] bg-white">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/tarotkortterapi.webp', { width: 800, quality: 85, fit: 'cover' })}
                  alt="Tarotkort omkring materiale om chakraer"
                  className="w-full h-auto object-cover"
                />
                <div className="p-3 bg-[#fcf8fb] text-xs text-[#9b1b66] text-center border-t border-[#edd5e8]">
                  Tarotkortene placeret omkring chakramaterialet under sessionen
                </div>
              </div>

              {/* Praktisk information */}
              <div className="bg-[#381d6e] text-[#faf5f9] rounded-3xl p-8 border border-[#5f2a99] shadow-lg space-y-5">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-widest text-[#e895c8] block mb-1">
                    Praktisk information
                  </span>
                  <h3 className="font-serif text-2xl font-semibold text-white">
                    Status på dine chakraer
                  </h3>
                </div>

                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Terapeut:</span>
                    <span className="font-semibold text-white">Lone Hvid</span>
                  </div>
                  <div className="flex items-start justify-between pb-2 border-b border-[#5f2a99] gap-3">
                    <span className="text-[#f7eefa]">Varighed:</span>
                    <span className="font-semibold text-white text-right">1-2 timer (individuelt tilpasset)</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Pris:</span>
                    <span className="font-semibold text-white">500 kr.</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Sted:</span>
                    <span className="font-semibold text-white text-right">Ibjergvej 12, 5792 Årslev</span>
                  </div>
                  <div className="flex items-start justify-between pb-2 border-b border-[#5f2a99] gap-2">
                    <span className="text-[#f7eefa]">Afbud:</span>
                    <span className="font-semibold text-white text-right">Senest 24 timer før på 30 29 79 02 (evt. sms)</span>
                  </div>
                </div>

                {/* Booking til Lone */}
                <div className="pt-3 border-t border-[#5f2a99] space-y-3">
                  <p className="text-xs text-[#f7eefa]">
                    Bestil tid hos Lone Hvid:
                  </p>
                  <div className="flex flex-col gap-2.5">
                    <a
                      href="tel:30297902"
                      className="flex items-center justify-center gap-3 w-full py-3.5 px-5 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-sm shadow transition-colors"
                    >
                      <i className="fa-solid fa-phone" aria-hidden="true"></i>
                      <span>Ring på 30 29 79 02</span>
                    </a>
                    <a
                      href="mailto:lone.hvid@hotmail.com"
                      className="flex items-center justify-center gap-3 w-full py-3 px-5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20 transition-colors"
                    >
                      <i className="fa-regular fa-envelope" aria-hidden="true"></i>
                      <span>Skriv til lone.hvid@hotmail.com</span>
                    </a>
                  </div>
                </div>

              </div>

            </div>

          </div>
        </div>
      </section>

      {/* BEHANDLING 2: TAROTKORT-TERAPI MED FOKUS PÅ PROBLEMLØSNING */}
      <section id="problemloesning" className="py-20 sm:py-24 bg-[#faf5f9] border-t border-[#edd5e8] scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#f0d9eb] text-[#9b1b66] text-xs font-semibold uppercase tracking-wider mb-4">
            <i className="fa-solid fa-bullseye text-[11px]" aria-hidden="true"></i>
            <span>Behandling 2</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            <div className="lg:col-span-7 space-y-6">
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#1f142e] tracking-tight">
                Tarotkort-terapi med fokus på problemløsning
              </h2>

              <div className="prose prose-stone text-[#4e3568] text-base leading-relaxed space-y-4">
                <p>
                  Terapeutisk og set gennem Tarotkortenes symboler gennemgår vi sammen dit problem eller en hindring i dit liv, som du gerne vil have løst. Vi kommer vidt omkring det omdrejningspunkt, du har valgt, og det er et samarbejde med de reflektioner, du oplever, og mine terapeutiske tilgange.
                </p>
                <p>
                  Det er en meget dyb proces med mange vinkler på både dit problem og dig selv. Der bliver ofte anvendt terapeutiske redskaber undervejs til at skabe løsninger eller frigørelse af forhindringer i dit liv. Du trækker 7-10 kort, efter hvad der kommer i fokus. Måske lyder det ikke af meget, men tro mig, hvert kort rummer essenser, der kan gøre dig klogere og give dig indsigter om dig selv, som du ikke er bevidst om.
                </p>
                <p>
                  En spændende måde at arbejde med sine problemer på, hvor jeg får meget positiv respons på resultaterne. Så har du et grundliggende problem, du gerne vil arbejde med og løse, så kan denne session være en rigtig god og spændende metode for dig at arbejde med i samarbejde med mine terapeutiske redskaber.
                </p>
              </div>

              <div className="p-5 bg-white rounded-2xl border border-[#edd5e8] text-sm text-[#4e3568] space-y-2">
                <p className="font-semibold text-[#1f142e]">
                  Nøgleelementer i problemløsende tarotterapi:
                </p>
                <ul className="space-y-1.5 text-xs sm:text-sm">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#d9238c]" aria-hidden="true"></i>
                    <span>Fokuseret arbejde på et konkret omdrejningspunkt eller problem i dit liv</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#d9238c]" aria-hidden="true"></i>
                    <span>Trækning af 7-10 kort med dybe symbolske essenser og refleksioner</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#d9238c]" aria-hidden="true"></i>
                    <span>Kombination af NLP, psykologiske redskaber og 40 års erfaring med symbolarbejde</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right: Praktisk information for Problemløsning */}
            <div className="lg:col-span-5 space-y-6">
              
              <div className="bg-[#381d6e] text-[#faf5f9] rounded-3xl p-8 border border-[#5f2a99] shadow-lg space-y-5">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-widest text-[#e895c8] block mb-1">
                    Praktisk information
                  </span>
                  <h3 className="font-serif text-2xl font-semibold text-white">
                    Fokus på problemløsning
                  </h3>
                </div>

                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Terapeut:</span>
                    <span className="font-semibold text-white">Lone Hvid</span>
                  </div>
                  <div className="flex items-start justify-between pb-2 border-b border-[#5f2a99] gap-3">
                    <span className="text-[#f7eefa]">Varighed:</span>
                    <span className="font-semibold text-white text-right">2-3 timer (individuelt tilpasset)</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Pris:</span>
                    <span className="font-semibold text-white">700 kr.</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Sted:</span>
                    <span className="font-semibold text-white text-right">Ibjergvej 12, 5792 Årslev</span>
                  </div>
                  <div className="flex items-start justify-between pb-2 border-b border-[#5f2a99] gap-2">
                    <span className="text-[#f7eefa]">Afbud:</span>
                    <span className="font-semibold text-white text-right">Senest 24 timer før på 30 29 79 02 (evt. sms)</span>
                  </div>
                </div>

                {/* Booking til Lone */}
                <div className="pt-3 border-t border-[#5f2a99] space-y-3">
                  <p className="text-xs text-[#f7eefa]">
                    Bestil tid hos Lone Hvid:
                  </p>
                  <div className="flex flex-col gap-2.5">
                    <a
                      href="tel:30297902"
                      className="flex items-center justify-center gap-3 w-full py-3.5 px-5 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-sm shadow transition-colors"
                    >
                      <i className="fa-solid fa-phone" aria-hidden="true"></i>
                      <span>Ring på 30 29 79 02</span>
                    </a>
                    <a
                      href="mailto:lone.hvid@hotmail.com"
                      className="flex items-center justify-center gap-3 w-full py-3 px-5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20 transition-colors"
                    >
                      <i className="fa-regular fa-envelope" aria-hidden="true"></i>
                      <span>Skriv til lone.hvid@hotmail.com</span>
                    </a>
                  </div>
                </div>

              </div>

            </div>

          </div>
        </div>
      </section>
    </>
  );
}
